import { useEffect, useRef } from 'react';

/**
 * Attaches IntersectionObserver to a section ref.
 * Elements with class `.reveal` inside it get `.is-visible` when they enter
 * the viewport.
 *
 * The subtree is watched too, so cards that appear later — when the audience
 * track is switched, say — get observed and animate in just like the ones
 * that were there on mount.
 */
export function useReveal() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    // Observing an element twice is a no-op, so a plain re-scan is enough.
    const scan = () =>
      section
        .querySelectorAll<HTMLElement>('.reveal:not(.is-visible)')
        .forEach((el) => observer.observe(el));

    scan();
    const mutations = new MutationObserver(scan);
    mutations.observe(section, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);

  return sectionRef;
}
